import { KeycloakProfile } from "keycloak-js";
import queryString from "query-string";
import React, { ReactElement, useCallback, useState } from "react";
import { LoaderIcon } from "react-hot-toast";
import { FormattedMessage } from "react-intl";
import { useHistory } from "react-router";
import { useTheme } from "styled-components";
import { v4 as uuidv4 } from "uuid";
import { AuthorizationVerifier } from "../../components/authorization-verifier";
import { IPropertyChainData, useChainData } from "../../components/chain-data-provider";
import { KeycloakSingleton } from "../../components/keycloak-provider";
import { useProperty } from "../../components/property-provider";
import { UserTokenSessionAPIPromiseClient } from "../../gen/witbooking/backoffice/usertokensession/v1/user_token_session_api_grpc_web_pb";
import {
  CreateUserTokenSessionRequest,
  UserTokenSession,
} from "../../gen/witbooking/backoffice/usertokensession/v1/user_token_session_api_pb";
import { useGrpcClient, usePropertyGrpcCall } from "../../lib/grpc";
import { SVGMenu } from "./assets";

export interface MenuItemProps {
  href?: string;
  route?: string;
  message: {
    id: string;
    defaultMessage: string;
  };
  target?: string;
  section?: string;
  icon?: string;
  hideItem?: false;
  witAdminRequired?: boolean;
  hasPermissions?: boolean;
  children?: ReactElement;
}

export function MenuItem({
  href,
  route,
  message,
  target,
  section,
  icon,
  witAdminRequired,
  hasPermissions,
}: MenuItemProps) {
  const history = useHistory();
  const isLegacy = !!href;
  const theme = useTheme();

  const handleClick = useCallback(() => {
    if (route) {
      history.push(route);
    } else {
      console.error("No route and no href on menu item. Will do nothing.");
    }
  }, [history, route]);

  return (
    <AuthorizationVerifier
      section={section}
      showForbiddenMessage={false}
      witAdminRequired={witAdminRequired}
      hasPermissions={hasPermissions}>
      <div className={`p-2 hover:bg-[${theme.greyBackground}]`}>
        <a
          className="cursor-pointer hover:no-underline flex justify-start items-center "
          href={isLegacy ? href : undefined}
          onClick={isLegacy ? undefined : handleClick}
          target={target}>
          {icon && SVGMenu[icon]}
          <span
            className={`${!!icon && "pl-2"} text-[${theme.textColor}] hover:text-[${
              theme.buttonColor
            }]`}>
            <FormattedMessage {...message} />
          </span>
        </a>
      </div>
    </AuthorizationVerifier>
  );
}

export function CallCenterLoyalty({
  message,
  propertyTicker,
  userProfile,
  engineBaseUrl,
  loyaltyBrand,
  icon,
}: any) {
  const chainData: IPropertyChainData | undefined | null = useChainData();
  const mainDomain = loyaltyBrand && loyaltyBrand.mainDomain;
  const engineDomain = mainDomain ? `https://${mainDomain}` : engineBaseUrl;
  const engineUrl = queryString.stringifyUrl({
    url: `${engineDomain}/${chainData?.isChain ? "chain" : "hotel"}/${propertyTicker}`,
    query: {
      encodedAffiliate: userProfile ? btoa(userProfile.email || "") : undefined,
      origin: "callcenter",
      disableAbTest: "1",
    },
  });
  return (
    <div className="flex justify-start items-center p-2 hover:bg-gray-100 text-[#5C5C5C]">
      {icon && SVGMenu[icon]}
      <span className="hover:text-[#2E3862]">
        <a
          className="text-[#5C5C5C] pl-2 cursor-pointer hover:no-underline "
          href={engineUrl}
          target="_blank"
          rel="noopener noreferrer">
          <FormattedMessage {...message} />
        </a>
      </span>
    </div>
  );
}

export function QuotationMenu({
  message,
  propertyTicker,
  engineBaseUrl,
  icon,
  isCallCenterUser,
  userAttributes,
  locale,
  userProfile,
}: {
  message: { id: string; defaultMessage: string };
  propertyTicker: string;
  engineBaseUrl: string;
  icon: string;
  isCallCenterUser: boolean;
  userAttributes: string;
  locale: string;
  userProfile?: (KeycloakProfile & { attributes?: Record<string, any> }) | undefined;
}) {
  const client = useGrpcClient(UserTokenSessionAPIPromiseClient);
  const createUserTokenSession = usePropertyGrpcCall(client, client.createUserTokenSession);
  const property = useProperty();
  const [isLoading, setIsLoading] = useState(false);
  const chainData: IPropertyChainData | undefined | null = useChainData();
  const url = `${engineBaseUrl}/${locale}/${
    chainData?.isChain ? "chain" : "hotel"
  }/${propertyTicker}/quotation`;

  const handleCreateUserSessionToken = useCallback(async () => {
    setIsLoading(true);

    const userTokenSession = new UserTokenSession();
    // Only read by a backoffice-manager older than WTT-12335; newer ones generate the id and return it.
    userTokenSession.setId(uuidv4());
    userTokenSession.setEmail(userProfile?.email ?? "");
    userTokenSession.setToken(property.token);
    // The RPT above authorizes Witbooking APIs; anything the quotation page needs to ask
    // Keycloak must go through the user's access token instead — since Keycloak 26.5 an RPT
    // replayed on the uma-ticket grant is rejected and kills the session
    // (keycloak/keycloak#50827). Read at call time: keycloak-js rotates it in place.
    userTokenSession.setAccessToken(KeycloakSingleton?.token ?? "");

    const request = new CreateUserTokenSessionRequest();
    request.setUserTokenSession(userTokenSession);

    const response = (await createUserTokenSession(request)).getId();
    if (!response) {
      return;
    }

    const query = queryString.stringifyUrl({
      url,
      query: { origin: isCallCenterUser ? "callcenter" : "backoffice", disableAbTest: "1" },
    });
    // In the fragment, the session id and the user's data never reach a server log or the Referer; the engine
    // reads them and removes them from the URL before third-party scripts load.
    const fragment = queryString.stringify({
      encodedAffiliate: userProfile ? btoa(userProfile.email || "") : undefined,
      encodedAffiliateAttributes: btoa(userAttributes),
      boSessionId: response,
    });
    const quotationUrl = `${query}#${fragment}`;
    window.open(quotationUrl, "_blank", "noopener noreferrer");
  }, [url, userProfile, isCallCenterUser, userAttributes, property.token, createUserTokenSession]);

  if (!userProfile?.email || !property.token) {
    return null;
  }

  return (
    <div
      className="flex justify-start items-center p-2 hover:bg-gray-100 text-[#5C5C5C]"
      onClick={handleCreateUserSessionToken}>
      {icon && SVGMenu[icon]}
      <div className="text-[#5C5C5C] pl-2 cursor-pointer hover:text-[#2E3862]">
        <FormattedMessage {...message} />
      </div>
      {isLoading && (
        <div className="ml-2">
          <LoaderIcon />
        </div>
      )}
    </div>
  );
}
